import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { JSONContent } from '@tiptap/core';

import type { GetClassStudentAnswersQuery } from '../../present/activity-answers.graphql.generated';
import { Field } from 'approot/activity-builder/components/response/response.constants';
import { getStudentDisplayName } from 'approot/students/logic/get-student-display-name';
import { getRichtextAnswer } from 'approot/activity-builder/components/shared/logic/get-richtext-answer';
import { Student, StudentProfile } from '../classroom.constants';
import { isRichtextValueEmpty } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import type { Pluck } from 'lib/gql/pluck';

export type StudentTextResponse = StudentProfile & {
  answers: {
    itemId: string;
    answer: JSONContent | null;
  }[];
};

export function getStudentResponseAnswers({
  studentsInClass,
  studentAnswers,
  sortedFields,
}: {
  studentsInClass: Student[];
  studentAnswers: Pluck<
    GetClassStudentAnswersQuery,
    'getClassStudentsAnswers'
  >[];
  sortedFields: Field[];
}): StudentTextResponse[] {
  const groupedAnswers = groupBy(studentAnswers, 'studentId');

  const studentTextResponses = studentsInClass.map(student => {
    const allAnswers = groupedAnswers[student.id] || [];

    const answers = sortedFields.map(field => {
      const fieldAnswer = allAnswers.find(
        ans => ans.answerReferences?.id === field.itemId
      );
      return {
        itemId: field.itemId,
        answer: getRichtextAnswer(fieldAnswer) || null,
      };
    });

    return {
      studentId: student.id,
      studentName: getStudentDisplayName(student),
      studentEmoji: student.emeji,
      answers,
    };
  });

  const sortedStudentTextResponses = sortBy(studentTextResponses, response =>
    response.studentName.toLowerCase()
  );

  const studentsWithAnswers = sortedStudentTextResponses.filter(student =>
    hasStudentResponseAnswer(student)
  );

  const studentsWithoutAnswers = sortedStudentTextResponses.filter(
    student => !hasStudentResponseAnswer(student)
  );

  return [...studentsWithAnswers, ...studentsWithoutAnswers];
}

export function hasStudentResponseAnswer(
  response: StudentTextResponse
): boolean {
  return response.answers.some(a => !isRichtextValueEmpty(a.answer));
}
